<template>
  <PageWrapper>
    <template #content>
      <div class="p-8">
        <div id="agent-search" class="@container max-w-6xl mx-auto">
          <form
            id="agent-search-bar"
            class="flex flex-col mt-4 p-2 bg-gray-50 border-2 border-gray-200 rounded-xl gap-2"
            @submit.prevent="search"
          >
            <fieldset class="grid grid-cols-1 @xl:grid-cols-3 gap-2">
              <div class="relative @xl:col-span-2">
                <MagnifyingGlass
                  class="pointer-events-none w-6 h-6 absolute top-5.5 left-1 m-1 fill-gray-300"
                />
                <label for="agent-alias" class="block truncate"
                  >Filter by agent alias</label
                >
                <input
                  type="text"
                  id="alias-name"
                  aria-label="Agent alias"
                  placeholder="abcd"
                  v-model="searchParams.alias"
                  class="w-full h-10 pl-10 transition-colors bg-white"
                  minlength="4"
                  maxlength="6"
                  ref="textInput"
                />
              </div>
              <div class="@xl:col-span-1 relative overflow-hidden">
                <label for="direction" class="block truncate">
                  Agent Permission Status
                </label>
                <select
                  name="direction"
                  aria-label="direction"
                  class="w-full peer bg-white"
                  v-model="searchParams.vendor_approved"
                >
                  <option value="all">Any</option>
                  <option value="approved">Granted</option>
                  <option value="not-approved">Not granted</option>
                </select>
              </div>
            </fieldset>
            <fieldset
              class="flex flex-col @xl:flex-row gap-2 justify-end text-center"
            >
              <button
                class="text-white bg-blue-600 hover:bg-blue-800"
                type="submit"
              >
                Search
              </button>
              <a
                class="block p-2 rounded-md text-white bg-gray-600 hover:bg-gray-800 hover:no-underline"
                download="domains.csv"
                :href="csvHref"
              >
                Download as CSV
              </a>
            </fieldset>
          </form>
          <hr class="my-4 border-gray-300" />
          <div
            v-if="!vendorSelected"
            class="rounded-lg px-2 py-1 mb-4 mx-auto border-2 border-dashed border-orange-400 bg-orange-50 text-orange-900 text-sm max-w-xl"
          >
            <span class="font-bold">Note:</span> Showing agents enrolled with
            any vendor. Please select a vendor to see more detailed results.
          </div>
          <div class="w-full text-center mt-12" v-if="loadingAgents">
            <LoadingSpinner />
          </div>
          <div class="text-gray-500" v-else-if="agents.length === 0">
            <div class="text-center font-semibold">No results</div>
            <div
              class="mx-auto max-w-xl mt-3 text-xs px-3 py-2 bg-gray-50 border-2 border-gray-200 rounded-lg"
            >
              <span class="font-bold">Note: </span>If you do not find the agent
              you are looking for, first check to see if you entered it
              correctly. If you did and it is still not appearing, they may not
              be enrolled for the program.

              <div class="mt-2">
                <span class="font-bold"
                  >* Agent Permission Status options:</span
                >
                <ul class="list-disc list-inside pl-2">
                  <li>
                    <span class="font-semibold">Any:</span> Search for any agent
                    enrolled with {{ vendor }}.
                  </li>
                  <li>
                    <span class="font-semibold">Granted:</span>
                    Search for agents who have granted {{ vendor }} permissions
                    in MySFDomain for one or more domains.
                  </li>
                  <li>
                    <span class="font-semibold">Not granted:</span>
                    Search for agents who are enrolled with {{ vendor }} but
                    have <span class="font-bold">not</span> granted
                    {{ vendor }} permissions in MySFDomain for any domains.
                  </li>
                </ul>
              </div>
            </div>
          </div>
          <ol v-else class="flex flex-col gap-2">
            <li v-for="agent in agents" :key="agent.alias">
              <RouterLink
                class="bg-gray-100 hover:bg-gray-200 transition-colors no-underline rounded-lg px-4 py-3 flex flex-col @xl:flex-row justify-between items-start @xl:items-center"
                :to="{
                  name: 'Agent Overview',
                  params: { alias: agent.alias },
                }"
              >
                <div class="flex gap-2 text-gray-800">
                  <span class="font-bold">{{ agent.full_name }}</span>
                  <span>({{ agent.alias }})</span>
                </div>

                <div
                  v-if="vendorSelected"
                  class="flex flex-col @xl:flex-row gap-2 items-start @xl:items-center text-gray-600"
                >
                  <span>
                    Domains granted
                    <span class="font-bold">{{ vendor }}</span>
                    access:</span
                  >
                  <span
                    class="rounded-lg px-2 py-1 text-sm font-semibold"
                    :class="{
                      'bg-red-200 text-red-900':
                        agent.granted_access_domain_count === 0,
                      'bg-blue-200 text-blue-900':
                        agent.granted_access_domain_count !== 0,
                    }"
                  >
                    {{ agent.granted_access_domain_count }} /
                    {{ agent.eligible_domain_count }}
                  </span>
                </div>
              </RouterLink>
            </li>
          </ol>
          <div v-if="agents.length && !loadingAgents" class="mt-2">
            <button
              class="text-white w-full mt-2"
              v-if="nextPageLink != null"
              @click.prevent="loadNextPage"
            >
              <LoadingSpinner v-if="loadingNextPage" gray small />
              <span v-else> Show More </span>
            </button>
            <div v-else class="mt-2 w-full text-center text-gray-500">
              No more results
            </div>
          </div>
        </div>
      </div>
    </template>
  </PageWrapper>
</template>

<script setup>
import { useUrlSearchParams } from "@vueuse/core";
import { storeToRefs } from "pinia";
import { computed, nextTick, onMounted, ref } from "vue";
import MagnifyingGlass from "@/components/icons/MagnifyingGlass.vue";
import LoadingSpinner from "@/components/LoadingSpinner.vue";
import PageWrapper from "@/components/PageWrapper.vue";

const searchParams = useUrlSearchParams("history", {
  initialValue: { alias: "", vendor_approved: "all" },
});

import useUserStore from "@/stores/user";

const userStore = useUserStore();
const { vendor, vendorKey } = storeToRefs(userStore);

const textInput = ref(null);

const vendorSelected = computed(() => {
  return vendorKey.value && vendorKey.value !== "all";
});

const apiRequestParams = computed(() => {
  let params = {
    alias: searchParams.alias,
  };

  switch (searchParams.vendor_approved) {
    case "approved":
      params.vendor_approved = true;
      break;
    case "not-approved":
      params.vendor_approved = false;
      break;
  }

  return params;
});

const agents = ref([]);
const loadingAgents = ref(false);

async function search() {
  loadingAgents.value = true;

  const params = new URLSearchParams(apiRequestParams.value);
  const response = await fetch(
    `/api/cmp/${vendorKey.value}/agents/?${params.toString()}`,
  );
  if (!response.ok) {
    throw new Error("Agent search failed");
  }
  const data = await response.json();
  nextPageLink.value = data.next;
  agents.value = data.results;

  loadingAgents.value = false;
}

const nextPageLink = ref(null);
const loadingNextPage = ref(false);

async function loadNextPage() {
  loadingNextPage.value = true;
  const response = await fetch(nextPageLink.value);
  if (!response.ok) {
    throw new Error("Agent search failed");
  }
  const data = await response.json();
  nextPageLink.value = data.next;
  agents.value.push(...data.results);
  loadingNextPage.value = false;
}

const csvHref = computed(() => {
  const params = new URLSearchParams({
    ...apiRequestParams.value,
    format: "csv",
    no_pagination: "true",
  });
  return `/api/cmp/${vendorKey.value}/agents/?${params.toString()}`;
});

onMounted(async () => {
  await nextTick();
  textInput.value.focus();
  if (searchParams.alias) {
    search();
  }
});
</script>
